import { useSyncExternalStore, type ComponentType } from 'react';

import { create } from 'zustand';

/**
 * A section of the «راهنما و پیشنهادها» hub contributed by another feature
 * (the smart guide registers «پیشنهادها» at order 10). `order < 100` renders
 * above «شروع کار».
 */
export interface HubSectionDef {
  key: string;
  order: number;
  title: string;
  Component: ComponentType<{ focusId: string | null }>;
  useVisible?: () => boolean;
  useBadgeCount?: () => number;
}

let sections: readonly HubSectionDef[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Registers a hub section. Call at MODULE IMPORT time only. Returns an
 * unregister function (tests). A second registration under the same key
 * replaces the first.
 */
export function registerHubSection(def: HubSectionDef): () => void {
  sections = [...sections.filter((s) => s.key !== def.key), def].sort((a, b) => a.order - b.order);
  emit();
  return () => {
    sections = sections.filter((s) => s !== def);
    useHubProbeStore.getState().forget(def.key);
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The registered sections, sorted by `order`. */
export function useHubSections(): readonly HubSectionDef[] {
  return useSyncExternalStore(
    subscribe,
    () => sections,
    () => sections,
  );
}

/**
 * What each section's own hooks reported. A section's `useVisible` and
 * `useBadgeCount` run inside ONE probe component per section (mounted by
 * `GuidanceRoot`), never in the caller of `useHubBadgeTotal`: registering a
 * section must not change any other component's hook order.
 */
interface ProbeState {
  visible: Record<string, boolean>;
  badge: Record<string, number>;
  report: (key: string, visible: boolean, badge: number) => void;
  forget: (key: string) => void;
}

export const useHubProbeStore = create<ProbeState>()((set) => ({
  visible: {},
  badge: {},
  report(key, visible, badge) {
    set((s) =>
      s.visible[key] === visible && s.badge[key] === badge
        ? s
        : { visible: { ...s.visible, [key]: visible }, badge: { ...s.badge, [key]: badge } },
    );
  },
  forget(key) {
    set((s) => {
      const visible = { ...s.visible };
      const badge = { ...s.badge };
      delete visible[key];
      delete badge[key];
      return { visible, badge };
    });
  },
}));

/** Sum of every visible section's badge count: drives the Help dot and its label. */
export function useHubBadgeTotal(): number {
  return useHubProbeStore((s) =>
    Object.entries(s.badge).reduce(
      (sum, [key, n]) => (s.visible[key] === false ? sum : sum + Math.max(0, n)),
      0,
    ),
  );
}

/** Whether a registered section is visible (unknown until its probe reports: hidden). */
export function useHubSectionVisible(key: string): boolean {
  return useHubProbeStore((s) => s.visible[key] === true);
}
